/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { useStore } from '../stores/common';
import { usePrimitiveStore } from '../stores/commonPrimitive';
import { useRefStore } from '../stores/commonRef';
import { Molecule } from '../models/Molecule';
import { Undoable } from './Undoable';

// Paste the copied (or cut) molecule at the last right-click point, as ONE
// undoable step. Shared by the context menu's Paste and the keyboard Ctrl/Cmd+V.
//
// The undo entry is recorded only when a molecule was actually added (no click
// point → nothing pasted → no phantom "Paste" on the stack). It keeps the PLACED
// molecule, so Redo puts it back where it was pasted — not at whatever spot the
// shared click point holds by then — and Undo removes that molecule by identity,
// not "the last slot". Returns whether anything was pasted.
export const pasteMoleculeWithUndo = (): boolean => {
  const { copiedMolecule, cutMolecule } = usePrimitiveStore.getState();
  const source = copiedMolecule ?? cutMolecule;
  const p = useRefStore.getState().clickPointRef?.current;
  if (!source || !p) return false;
  const name = copiedMolecule ? 'Paste Copied Molecule' : 'Paste Cut Molecule';
  const placed = Molecule.clone(source);
  placed.setCenter(p);
  let current: Molecule | null = null;

  const add = () => {
    // A fresh clone each time: the store freezes what it holds, and the pasted
    // copy must not share atoms with `placed` (kept for the next Redo).
    const m = Molecule.clone(placed);
    useStore.getState().set((state) => {
      state.projectState.testMolecules.push(m);
    });
    current = m;
    useRefStore.getState().warnIfTooManyAtoms?.(m.atoms.length);
    usePrimitiveStore.getState().setChanged(true);
  };

  const remove = () => {
    const index = current ? useStore.getState().projectState.testMolecules.indexOf(current) : -1;
    current = null;
    if (index < 0) return;
    useStore.getState().set((state) => {
      state.projectState.testMolecules.splice(index, 1);
    });
    // Selection indices may point at (or past) the removed slot — clear them,
    // as Cut / Delete do.
    usePrimitiveStore.getState().set((state) => {
      state.pickedMoleculeIndex = -1;
      state.pickedMoleculeIndices = new Set<number>();
      state.pickedAtomIndex = -1;
      state.pickedAtomIndices = new Set<number>();
    });
    usePrimitiveStore.getState().setChanged(true);
  };

  add();
  const undoable: Undoable = { name, timestamp: Date.now(), undo: remove, redo: add };
  useStore.getState().addUndoable(undoable);
  const { loggable, logAction } = useStore.getState();
  if (loggable) logAction(name);
  return true;
};
